import { TradingLevels } from '../types/trading';

/**
 * Helper function to detect if a number is likely a date, percentage, or range
 */
function isDateOrPercentage(context: string, value: number): boolean {
  const lowerContext = context.toLowerCase();
  
  // Enhanced date detection - catch ordinal numbers followed by time indicators
  if (/\b\d{1,2}(st|nd|rd|th)\b/.test(lowerContext)) {
    return true;
  }
  
  // Catch standalone date numbers in common contexts
  if (/(on the |since |from |until |by |during )\d{1,2}\b/.test(lowerContext)) {
    return true;
  }
  
  // Check for percentage indicators
  if (lowerContext.includes('%') || lowerContext.includes('percent')) {
    return true;
  }
  
  // Check for range descriptions
  if (lowerContext.includes('range of') || lowerContext.includes('points')) {
    return true;
  }
  
  // Very small values are likely not price levels (but don't constrain upper bound)
  if (value < 0.001) {
    return true;
  }
  
  return false;
}

/**
 * Get minimum price threshold based on asset type
 */
function getAssetMinThreshold(text: string): number {
  const lowerText = text.toLowerCase();
  
  // Bitcoin - should be in thousands
  if (lowerText.includes('bitcoin') || lowerText.includes('btc')) {
    return 1000;
  }
  
  // Ethereum - should be in hundreds
  if (lowerText.includes('ethereum') || lowerText.includes('eth')) {
    return 100;
  }
  
  // Major stocks like AAPL, MSFT, GOOGL - typically over $50
  if (/(apple|aapl|microsoft|msft|google|googl|amazon|amzn|nvidia|nvda|tesla|tsla)/.test(lowerText)) {
    return 50;
  }
  
  // SPY, QQQ ETFs - typically over $200
  if (/(spy|qqq)/.test(lowerText)) {
    return 200;
  }
  
  // Default minimum for unknown assets
  return 0.1;
}

/**
 * Detects if the AI response contains trading analysis data
 */
export function detectTradingResponse(text: string): boolean {
  const tradingKeywords = [
    'support',
    'resistance',
    'fibonacci',
    'swing high',
    'swing low',
    'retracement',
    'extension',
    'technical',
    'chart',
    'price level'
  ];

  const lowerText = text.toLowerCase();
  const keywordMatches = tradingKeywords.filter(keyword => 
    lowerText.includes(keyword)
  ).length;

  // Require at least 3 trading keywords and some numerical values
  const hasNumbers = /\d{1,5}(?:\.\d{1,2})?/.test(text);
  
  return keywordMatches >= 3 && hasNumbers;
}

/**
 * Extracts numerical values from text based on context keywords
 */
export function parseNumericalValues(text: string): number[] {
  // Match numbers with optional decimal places (e.g., 23450, 23.45, 23,450.25)
  const numberPattern = /\d{1,5}(?:,\d{3})*(?:\.\d{1,2})?/g;
  const matches = text.match(numberPattern) || [];
  
  return matches
    .map(match => parseFloat(match.replace(/,/g, ''))) // Remove commas
    .filter(num => !isNaN(num) && num > 0) // Filter valid positive numbers
    .sort((a, b) => b - a); // Sort descending
}

/**
 * Extracts support levels from AI response text with improved context analysis
 */
export function extractSupportLevels(text: string): number[] {
  const minThreshold = getAssetMinThreshold(text);
  
  const supportPatterns = [
    // Standard support patterns
    /support[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?support/gi,
    // Additional patterns for better detection
    /low[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /bounce[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /buying\s+interest[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // Pattern for explicit level mentions like "~$4,200: A level where..."
    /~?\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:level where|support|bounce|buying)/gi,
    /floor[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // Enhanced patterns for explicit level mentions
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:indicated|marked|shown)/gi,
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:range|panel)/gi,
    // Pattern for "find support" or "found support"
    /(?:find|found)[^.]*?\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "Intermediate Support" and "Major Support" mentions
    /intermediate\s+support[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /major\s+support[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?intermediate\s+support/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?major\s+support/gi,
    // NEW: Enhanced patterns for bullet point format support levels
    /~\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?):[^.]*?(?:support|near-term|level|consolidation|area)/gi,
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?):[^.]*?(?:support|near-term|level|consolidation|area|acted as)/gi,
    // NEW: Pattern for "near-term support level"
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?near-term\s+support/gi,
    /near-term\s+support[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "consolidation area"
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?consolidation\s+area/gi,
    /consolidation\s+area[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "acted as" descriptions
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?acted\s+as[^.]*?(?:consolidation|support)/gi,
    // NEW: Pattern for "this level" references
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?this\s+level[^.]*?acted/gi
  ];

  const levels: number[] = [];
  
  supportPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      // Handle multiple capture groups
      for (let i = 1; i < match.length; i++) {
        if (match[i]) {
          const value = parseFloat(match[i].replace(/,/g, ''));
          // Filter out invalid values: must be positive number and above asset minimum
          if (!isNaN(value) && value >= minThreshold) {
            const fullMatch = match[0];
            const beforeContext = text.substring(Math.max(0, match.index - 100), match.index);
            const afterContext = text.substring(match.index + match[0].length, match.index + match[0].length + 100);
            const fullContext = beforeContext + fullMatch + afterContext;
            
            // Skip if it looks like a date, percentage, or range description
            if (!isDateOrPercentage(fullContext, value)) {
              const contextLower = fullContext.toLowerCase();
              
              // Smart context analysis for support levels
              const isResistanceContext = contextLower.includes('resistance') || 
                                          contextLower.includes('struggle') || 
                                          contextLower.includes('ceiling') ||
                                          contextLower.includes('peak') ||
                                          contextLower.includes('highest');
              
              const isSwingHigh = contextLower.includes('swing high') || contextLower.includes('swing high:');
              
              // Exclude resistance contexts and swing highs from support levels
              if (!isResistanceContext && !isSwingHigh) {
                levels.push(value);
              }
            }
          }
        }
      }
    }
  });

  const uniqueLevels = levels.filter((value, index, self) => self.indexOf(value) === index);
  return uniqueLevels.sort((a, b) => b - a); // Remove duplicates and sort descending
}

/**
 * Extracts resistance levels from AI response text with improved context analysis
 */
export function extractResistanceLevels(text: string): number[] {
  const minThreshold = getAssetMinThreshold(text);
  
  const resistancePatterns = [
    // Standard resistance patterns
    /resistance[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?resistance/gi,
    // Additional patterns for better detection
    /high[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /peak[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /ceiling[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /struggle[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // Pattern for explicit level mentions like "~$4,680: The highest peak..."
    /~?\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:highest|peak|resistance|ceiling|struggle)/gi,
    // Pattern for "acting as" descriptions
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?acting as[^.]*?resistance/gi,
    // Enhanced patterns for explicit level mentions
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[:\s-].*?(?:zone|level|area|point)/gi,
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)\s*-\s*\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi, // Range like $328-$330
    /~\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi, // ~$346 format
    // Pattern for "This level marks" or "This represents"
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:marks|represents)[^.]*?(?:high|peak|resistance)/gi,
    // Pattern for levels mentioned with context
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?(?:acted as|breaking down|may now act)/gi,
    // Pattern for explicit line items
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?:/gi,
    // Pattern for "reached around"
    /reached around[^.]*?\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "Intermediate Resistance" and "Minor Resistance" mentions
    /intermediate\s+resistance[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /minor\s+resistance[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?intermediate\s+resistance/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?minor\s+resistance/gi,
    // NEW: Pattern for "Primary Resistance" mentions
    /primary\s+resistance[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?primary\s+resistance/gi,
    // NEW: Enhanced patterns for bullet point format resistance levels
    /~\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?):[^.]*?(?:resistance|primary|significant|previous|high|peak)/gi,
    /\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?):[^.]*?(?:resistance|primary|significant|previous|high|peak|reached|failed)/gi,
    // NEW: Pattern for "significant previous high"
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?significant\s+previous\s+high/gi,
    /significant\s+previous\s+high[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "most recent high"
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?most\s+recent\s+high/gi,
    /most\s+recent\s+high[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "failed to break above"
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?failed\s+to\s+break\s+above/gi,
    /failed\s+to\s+break\s+above[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "reached around" specific dates
    /(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)[^.]*?reached\s+around\s+the\s+\d+th/gi,
    // NEW: Pattern for "DAY'S RANGE" confirmation
    /day'?s?\s+range[^.]*?high\s+of\s+\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "around the Xth" time references
    /around\s+the\s+\d+th[^.]*?\$(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi,
    // NEW: Pattern for "current day" high references
    /current\s+day[^.]*?high\s+of\s+\$(\d{1,6}(?:,\d{1,2})?)/gi
  ];

  const levels: number[] = [];
  
  resistancePatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      // Handle multiple capture groups (for ranges like $328-$330)
      for (let i = 1; i < match.length; i++) {
        if (match[i]) {
          const value = parseFloat(match[i].replace(/,/g, ''));
          // Filter out invalid values: must be positive number and above asset minimum
          if (!isNaN(value) && value >= minThreshold) {
            const fullMatch = match[0];
            const beforeContext = text.substring(Math.max(0, match.index - 100), match.index);
            const afterContext = text.substring(match.index + match[0].length, match.index + match[0].length + 100);
            const fullContext = beforeContext + fullMatch + afterContext;
            
            // Skip if it looks like a date, percentage, or range description
            if (!isDateOrPercentage(fullContext, value)) {
              const contextLower = fullContext.toLowerCase();
              
              // Smart context analysis for resistance levels
              const mentionsResistance = contextLower.includes('resistance') || 
                                         contextLower.includes('struggle') || 
                                         contextLower.includes('ceiling') ||
                                         contextLower.includes('peak') ||
                                         contextLower.includes('highest');
              
              const mentionsSupport = contextLower.includes('support') || 
                                     contextLower.includes('bounce') || 
                                     contextLower.includes('buying') ||
                                     contextLower.includes('floor');
              
              // Include levels that:
              // 1. Explicitly mention resistance terms
              // 2. Are described as transitioning from support to resistance  
              // 3. Don't mention support at all (likely resistance by context)
              // But exclude swing lows that are clearly support-only
              const isTransitionLevel = mentionsSupport && mentionsResistance && 
                                       (contextLower.includes('may now act') || 
                                        contextLower.includes('before breaking') ||
                                        contextLower.includes('could now serve') ||
                                        contextLower.includes('likely to be') ||
                                        contextLower.includes('now serve as') ||
                                        contextLower.includes('act as resistance'));
              const isSwingLow = contextLower.includes('swing low') || contextLower.includes('low on day');
              
              // Also check if this is in a "Resistance Levels" section
              const inResistanceSection = contextLower.includes('resistance levels') || 
                                         contextLower.includes('potential price ceilings') ||
                                         contextLower.includes('resistance (potential');
              
              if ((mentionsResistance || isTransitionLevel || inResistanceSection || !mentionsSupport) && !isSwingLow) {
                levels.push(value);
              }
            }
          }
        }
      }
    }
  });

  const uniqueLevels = levels.filter((value, index, self) => self.indexOf(value) === index);
  return uniqueLevels.sort((a, b) => b - a); // Remove duplicates and sort descending
}

/**
 * Extracts swing high and low values from AI response
 */
export function extractSwingPoints(text: string): { swingHigh: number; swingLow: number } {
  const minThreshold = getAssetMinThreshold(text);
  const swingHighPattern = /swing\s+high[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi;
  const swingLowPattern = /swing\s+low[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi;
  
  // Also look for "current area high" and similar patterns
  const alternateHighPattern = /(current (?:area )?high)[^.]*?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi;

  let swingHigh = 0;
  let swingLow = 0;

  // Check explicit swing high mentions
  let highMatch;
  while ((highMatch = swingHighPattern.exec(text)) !== null) {
    const value = parseFloat(highMatch[1].replace(/,/g, ''));
    const fullMatch = highMatch[0];
    const beforeContext = text.substring(Math.max(0, highMatch.index - 50), highMatch.index);
    const afterContext = text.substring(highMatch.index + highMatch[0].length, highMatch.index + highMatch[0].length + 50);
    const fullContext = beforeContext + fullMatch + afterContext;
    
    // Only accept positive price levels above minimum threshold
    if (!isNaN(value) && value >= minThreshold && value > swingHigh) {
      // Skip if it looks like a date
      if (!isDateOrPercentage(fullContext, value)) {
        swingHigh = value;
      }
    }
  }
  
  // Check alternate high patterns like "current area high near $116,000"
  let altHighMatch;
  while ((altHighMatch = alternateHighPattern.exec(text)) !== null) {
    const value = parseFloat(altHighMatch[2].replace(/,/g, ''));
    const fullMatch = altHighMatch[0];
    const beforeContext = text.substring(Math.max(0, altHighMatch.index - 50), altHighMatch.index);
    const afterContext = text.substring(altHighMatch.index + altHighMatch[0].length, altHighMatch.index + altHighMatch[0].length + 50);
    const fullContext = beforeContext + fullMatch + afterContext;
    
    if (!isNaN(value) && value >= minThreshold && value > swingHigh) {
      if (!isDateOrPercentage(fullContext, value)) {
        swingHigh = value;
      }
    }
  }

  let lowMatch;
  while ((lowMatch = swingLowPattern.exec(text)) !== null) {
    const value = parseFloat(lowMatch[1].replace(/,/g, ''));
    const fullMatch = lowMatch[0];
    const beforeContext = text.substring(Math.max(0, lowMatch.index - 50), lowMatch.index);
    const afterContext = text.substring(lowMatch.index + lowMatch[0].length, lowMatch.index + lowMatch[0].length + 50);
    const fullContext = beforeContext + fullMatch + afterContext;
    
    // Only accept positive price levels above minimum threshold
    if (!isNaN(value) && value >= minThreshold && (swingLow === 0 || value < swingLow)) {
      // Skip if it looks like a date
      if (!isDateOrPercentage(fullContext, value)) {
        swingLow = value;
      }
    }
  }

  return { swingHigh, swingLow };
}

/**
 * Calculates Fibonacci levels based on swing points
 */
/**
 * Extracts AI-specified Fibonacci retracement levels from response text
 */
export function extractAIFibonacciRetracements(text: string) {
  const fibLevels = {
    level236: 0,
    level382: 0,
    level500: 0,
    level618: 0,
    level786: 0
  };

  // Patterns to match AI-specified Fibonacci retracement levels
  const patterns = [
    { level: 'level236', pattern: /23\.6%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi },
    { level: 'level382', pattern: /38\.2%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi },
    { level: 'level500', pattern: /50\.?0?%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi },
    { level: 'level618', pattern: /61\.8%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi },
    { level: 'level786', pattern: /78\.6%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi }
  ];

  patterns.forEach(({ level, pattern }) => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const value = parseFloat(match[1].replace(/,/g, ''));
      if (!isNaN(value) && value > 0) {
        fibLevels[level as keyof typeof fibLevels] = value;
        break; // Take the first match for each level
      }
    }
  });

  return fibLevels;
}

/**
 * Extracts AI-specified Fibonacci extension levels from response text
 */
export function extractAIFibonacciExtensions(text: string) {
  const fibExtensions = {
    level1272: 0,
    level1618: 0
  };

  // Patterns to match AI-specified Fibonacci extension levels
  const patterns = [
    { level: 'level1272', pattern: /127\.2%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi },
    { level: 'level1618', pattern: /161\.8%[^$]*\$?(\d{1,6}(?:,\d{3})*(?:\.\d{1,2})?)/gi }
  ];

  patterns.forEach(({ level, pattern }) => {
    let match;
    while ((match = pattern.exec(text)) !== null) {
      const value = parseFloat(match[1].replace(/,/g, ''));
      if (!isNaN(value) && value > 0) {
        fibExtensions[level as keyof typeof fibExtensions] = value;
        break; // Take the first match for each level
      }
    }
  });

  return fibExtensions;
}

export function calculateFibonacciLevels(swingHigh: number, swingLow: number) {
  const range = swingHigh - swingLow;
  
  return {
    level236: swingLow + (range * 0.236),
    level382: swingLow + (range * 0.382),
    level500: swingLow + (range * 0.500),
    level618: swingLow + (range * 0.618),
    level786: swingLow + (range * 0.786)
  };
}

/**
 * Calculates Fibonacci extension levels
 */
export function calculateFibonacciExtensions(swingHigh: number, swingLow: number) {
  const range = swingHigh - swingLow;
  
  return {
    level1272: swingHigh - (range * 1.272),
    level1618: swingHigh - (range * 1.618)
  };
}

/**
 * Main function to extract all trading levels from AI response
 */
export function extractTradingLevels(text: string): TradingLevels {
  const supportLevels = extractSupportLevels(text);
  const resistanceLevels = extractResistanceLevels(text);
  const { swingHigh, swingLow } = extractSwingPoints(text);

  // If no explicit swing points found, try to infer from support/resistance levels
  const finalSwingHigh = swingHigh > 0 ? swingHigh : (resistanceLevels[0] || 0);
  const finalSwingLow = swingLow > 0 ? swingLow : (supportLevels[supportLevels.length - 1] || 0);

  // Try to extract AI-specified Fibonacci levels first
  const aiRetracements = extractAIFibonacciRetracements(text);
  const aiExtensions = extractAIFibonacciExtensions(text);
  
  // Check if we found any AI-specified levels (non-zero values)
  const hasAIRetracements = Object.values(aiRetracements).some(level => level > 0);
  const hasAIExtensions = Object.values(aiExtensions).some(level => level > 0);
  
  // Use AI-specified levels if available, otherwise calculate them
  const fibonacciRetracements = hasAIRetracements ? aiRetracements : calculateFibonacciLevels(finalSwingHigh, finalSwingLow);
  const fibonacciExtensions = hasAIExtensions ? aiExtensions : calculateFibonacciExtensions(finalSwingHigh, finalSwingLow);

  return {
    supportLevels,
    resistanceLevels,
    fibonacciRetracements,
    fibonacciExtensions,
    swingHigh: finalSwingHigh,
    swingLow: finalSwingLow
  };
}

/**
 * Validates extracted trading levels for reasonableness
 */
export function validateTradingLevels(levels: TradingLevels): boolean {
  // Check if we have at least some data
  const hasData = levels.swingHigh > 0 && levels.swingLow > 0;
  const hasLevels = levels.supportLevels.length > 0 || levels.resistanceLevels.length > 0;
  
  // Check if swing high is greater than swing low
  const validRange = levels.swingHigh > levels.swingLow;
  
  return hasData && hasLevels && validRange;
}